import { Col, Form, Input, Row, Select, Tooltip } from 'antd';
import LicensePlateFormField from '../../LicensePlateFormField';
import { useEffect } from 'react';
import {
  useVehicleFragaByChassi,
  useVehicleFragaList,
} from '@/core/domain/people/Vehicle/Vehicle.hooks';
import { Model } from '@/core/domain/people/BrandsModelsVersions/BrandsModelsVersions.types';
import { useVehiclesFilterMutation } from '@/core/domain/people/Customers/Customers.hooks';
import { getAccessoryId } from '../getAccessoryId';
import FloatingFormItem from '../../FloatingFormItem';
import { AuthorityDefault } from '@/types/Authority/Authority.Default';
import {
  CustomerVehicle,
  VehicleFormData,
} from '@/core/domain/people/CustomerVehicles/CustomerVehicles.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import VehicleModalConfirm from '../VehicleModalConfirm';
import { useDebounce, useDisclosure } from '@/hooks';
import { VehicleFragaResponse } from '@/core/domain/people/Vehicle/Vehicle.types';
import Authorities from '@/types/Authority';

const DELETE_KEY_CODE = 8;

interface Props {
  consultLicensePlate?: boolean;
  licensePlate?: string;
  vehicle?: CustomerVehicle;
}

const VehicleLicensePlate: React.FC<Props> = ({
  consultLicensePlate = false,
  vehicle,
  ...props
}) => {
  const form = Form.useFormInstance<VehicleFormData>();
  const searchBy = Form.useWatch('searchBy', form);
  const searchLicensePlate = Form.useWatch('licensePlate', form);
  const searchChassi = Form.useWatch('chassi', form);

  const searchLicensePlateDebounce = useDebounce(searchLicensePlate);

  const [vehicleConfirm, vehicleConfirmAction] = useDisclosure();

  const fraga = useVehicleFragaList();
  const vehiclesFilter = useVehiclesFilterMutation();
  const findByChassi = useVehicleFragaByChassi();

  useEffect(() => {
    if (consultLicensePlate && !vehiclesFilter.isLoading) {
      vehicleSearch(props.licensePlate || '');
    }
  }, [consultLicensePlate, props.licensePlate]);

  useEffect(() => {
    if (searchLicensePlateDebounce && !vehiclesFilter.isLoading) {
      vehicleSearch(searchLicensePlateDebounce);
    }
  }, [searchLicensePlateDebounce]);

  useEffect(() => {
    if (
      searchChassi &&
      searchChassi.length >= 17 &&
      searchBy === 'Chassi' &&
      !findByChassi.isLoading
    ) {
      chassiSearch(searchChassi);
    }
  }, [searchChassi]);

  async function getVehicle(licensePlate: string) {
    const { data } = await vehiclesFilter.mutateAsync({
      licensePlate,
      active: true,
      rows: 1,
    });

    if (data.length && data[0].licensePlate === licensePlate) return data[0];

    return null;
  }

  function setVehicleFraga(vehicle: VehicleFragaResponse) {
    form.setFieldsValue({
      ...vehicle,
      brandId: vehicle?.model?.brand?.id,
      modelId: vehicle?.model?.id,
      fuelId: vehicle?.fuel?.id,
      chassi: vehicle?.chassi,
      colorId: vehicle?.color?.id,
      yearFabrication: vehicle?.yearFabrication
        ? String(vehicle?.yearFabrication)
        : undefined,
      yearModel: vehicle?.yearModel ? String(vehicle?.yearModel) : undefined,
    });
  }

  async function vehicleSearch(licensePlate: string) {
    const customerVehicle =
      vehicle?.licensePlate === licensePlate
        ? vehicle
        : await getVehicle(licensePlate);

    if (customerVehicle) {
      const obj = {
        ...customerVehicle,
        yearFabrication: customerVehicle?.yearFabrication
          ? String(customerVehicle.yearFabrication)
          : undefined,
        yearModel: customerVehicle?.yearModel
          ? String(customerVehicle.yearModel)
          : undefined,
        colorId: customerVehicle?.color?.id,
        fuelId: customerVehicle?.fuel?.id,
        chassi: customerVehicle?.chassi,
        fleet: customerVehicle?.fleet,
        renavam: customerVehicle?.renavam,
        accessories: !!customerVehicle?.accessories?.length
          ? customerVehicle.accessories.map((accessory) =>
              getAccessoryId(accessory),
            )
          : undefined,
        customerId: customerVehicle?.customerId,
        vehicleId: customerVehicle?.id,
        image: customerVehicle.image,
        horsePower: !!customerVehicle?.horsePower?.length
          ? customerVehicle.horsePower
          : undefined,
      };

      form.setFieldsValue(obj);

      return setFormModel(customerVehicle.model);
    } else {
      form.setFieldsValue({ customerId: undefined, vehicleId: undefined });
    }

    const validateFragaPlate = /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/;

    if (validateFragaPlate.test(licensePlate)) {
      const fragaData = await fraga.mutateAsync(licensePlate);

      if (!!fragaData?.data?.length) {
        if (fragaData.data.length > 1) {
          return vehicleConfirmAction.open();
        }

        const firstVehicle = fragaData.data[0];

        setVehicleFraga(firstVehicle);
      }
    }
  }

  function setFormModel(model: Model) {
    const hasModelName = !!model.name?.trim();
    const hasBrandName = !!model.brand?.name?.trim();

    form.setFieldsValue({
      modelId: hasModelName ? model.id : undefined,
      modelName: hasModelName ? model.name : undefined,
      brandId: hasBrandName ? model?.brand?.id : undefined,
      brandName: hasBrandName ? model?.brand?.name : undefined,
    });
  }

  async function chassiSearch(chassi: string) {
    try {
      const vehicleWithChassi =
        vehicle?.chassi === chassi
          ? vehicle
          : await findByChassi.mutateAsync(chassi);

      if (vehicleWithChassi?.licensePlate) {
        vehicleSearch(vehicleWithChassi.licensePlate);
        form.setFieldValue('licensePlate', vehicleWithChassi.licensePlate);
      }
    } catch {}
  }

  function resetFields() {
    if (!!fraga.data) {
      fraga.reset();
    }

    form.resetFields([
      'brandId',
      'modelId',
      'modelName',
      'brandName',
      'yearFabrication',
      'yearModel',
      'accessories',
      searchBy === 'Placa' ? 'chassi' : 'licensePlate',
      'colorId',
      'fleet',
      'fuelId',
      'km',
      'renavam',
      'customerId',
      'vehicleId',
      'image',
      'imageUpload',
      'model',
      'release',
      'cylinders',
      'engineCapacity',
      'engineName',
      'engineTechnicalCode',
      'horsePower',
      'valves',
    ]);
  }

  return (
    <>
      <Row gutter={[24, 16]}>
        <Col
          span={24}
          md={
            hasPermissions([AuthorityDefault.DEFAULT_INTEGRATION_FRAGA])
              ? 12
              : 24
          }
        >
          {searchBy === 'Placa' ? (
            <LicensePlateFormField
              label="Procurar"
              name="licensePlate"
              disabledFields={
                fraga.isLoading ||
                consultLicensePlate ||
                !hasPermissions([
                  Authorities.REGISTER_CUSTOMER_VEHICLE_EDIT,
                  Authorities.REGISTER_CUSTOMER_VEHICLE_ADD,
                ])
              }
              maskedInputProps={{
                onKeyDown: ({ keyCode }) => {
                  if (keyCode === DELETE_KEY_CODE) return resetFields();
                },
              }}
            />
          ) : (
            <Tooltip
              trigger="click"
              title="Ao buscar pelo chassi, placa retornará no padrão mercosul"
            >
              <div>
                <FloatingFormItem
                  label="Procurar"
                  name="chassi"
                  getValueFromEvent={(value) => {
                    if (value.target.value?.length < 17) {
                      resetFields();
                    }

                    return value.target.value.toUpperCase();
                  }}
                  rules={[
                    {
                      required: true,
                      message: 'O campo de chassi é obrigatório.',
                    },
                    {
                      pattern: /^[A-HJ-NPR-Z0-9]{17}$/,
                      message:
                        'O chassi deve conter 17 caracteres alfanuméricos.',
                    },
                  ]}
                >
                  <Input
                    disabled={fraga.isLoading || findByChassi.isLoading}
                    maxLength={17}
                  />
                </FloatingFormItem>
              </div>
            </Tooltip>
          )}
        </Col>

        <Col
          span={24}
          md={12}
          hidden={!hasPermissions([AuthorityDefault.DEFAULT_INTEGRATION_FRAGA])}
        >
          <FloatingFormItem
            name="searchBy"
            label="Procurar por"
            initialValue="Placa"
          >
            <Select
              disabled={consultLicensePlate}
              options={[{ value: 'Placa' }, { value: 'Chassi' }]}
              onChange={() =>
                form.resetFields([
                  'brandId',
                  'modelId',
                  'modelName',
                  'brandName',
                  'yearFabrication',
                  'yearModel',
                  'accessories',
                  'chassi',
                  'licensePlate',
                  'colorId',
                  'fleet',
                  'fuelId',
                  'km',
                  'renavam',
                  'image',
                  'imageUpload',
                  'model',
                  'release',
                  'cylinders',
                  'engineCapacity',
                  'engineName',
                  'engineTechnicalCode',
                  'horsePower',
                  'valves',
                ])
              }
            />
          </FloatingFormItem>
        </Col>
      </Row>

      <VehicleModalConfirm
        open={vehicleConfirm}
        onCancel={vehicleConfirmAction.close}
        vehicles={fraga.data?.data}
        onConfirm={setVehicleFraga}
      />
    </>
  );
};

export default VehicleLicensePlate;
